import { isMatch, isValid, parse, parseISO } from 'date-fns';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import 'intl-pluralrules';
import { initReactI18next } from 'react-i18next/initReactI18next';

import { i18nConfig } from '@sola/schema/lib/i18n-config.ts';
import { endsWithVowel } from '@sola/utils/koreanGrammar.ts';

import { LOCALE_FORMATTERS, isLocaleFormat } from './formatters.ts';

const stripEmptyDigits = (value: number, lang?: string) => {
  const amount =
    Math.floor(value) === value
      ? Intl.NumberFormat(lang, {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }).format(value)
      : Intl.NumberFormat(lang, {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 2,
        }).format(value);
  return amount;
};

const formatCurrency = (
  value: number,
  lang?: string,
  options?: {
    useNegativeParens?: boolean;
  },
) => {
  const amount = +value;
  if (Number.isNaN(amount)) {
    return '';
  }

  const { useNegativeParens } = options || {};
  const isNegative = amount < 0;

  let formattedAmount = Intl.NumberFormat(lang, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

  if (useNegativeParens && isNegative) {
    formattedAmount = `(${formattedAmount.replace('-', '')})`;
  }

  return formattedAmount;
};

/**
 * Parses a date string and prepares a Date object
 */
const parseDate = (value: string, preserveCalendarDate?: boolean) => {
  // Preserves a calendar date by creating a UTC date for that day.
  if (preserveCalendarDate) {
    const datePart = value.slice(0, 10);
    if (isMatch(datePart, 'yyyy-MM-dd')) {
      const parsedDate = parse(datePart, 'yyyy-MM-dd', new Date());
      if (isValid(parsedDate)) {
        return new Date(
          Date.UTC(
            parsedDate.getFullYear(),
            parsedDate.getMonth(),
            parsedDate.getDate(),
          ),
        );
      }
    }
  }

  const parsedIsoDate = parseISO(value);
  if (isValid(parsedIsoDate)) {
    return parsedIsoDate;
  }

  return new Date(value);
};

type DateFormatPreset = 'long' | 'month-year' | 'yearless';

const DATE_FORMAT_PRESETS: Record<
  DateFormatPreset,
  Intl.DateTimeFormatOptions
> = {
  long: {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  },
  'month-year': {
    year: 'numeric',
    month: 'long',
  },
  yearless: {
    month: 'short',
    day: 'numeric',
  },
};

/**
 * Formats a date-like value for translations.
 * When `preserveCalendarDate` is enabled, callers must pass the original
 * string value so the formatter can preserve the intended calendar date.
 */
const formatDate = (
  format: DateFormatPreset,
  value: Date | string,
  lang?: string,
  options?: {
    preserveCalendarDate?: boolean;
    timeZone?: string;
  },
) => {
  if (
    options?.preserveCalendarDate &&
    value instanceof Date &&
    process.env.NODE_ENV === 'development'
  ) {
    throw new Error(
      'When preserveCalendarDate is enabled, pass the original date string instead of a Date object.',
    );
  }

  const date =
    value instanceof Date
      ? value
      : parseDate(value, options?.preserveCalendarDate);
  if (!isValid(date)) {
    return '';
  }

  const timeZone = options?.preserveCalendarDate
    ? options.timeZone || 'UTC'
    : options?.timeZone;

  return date.toLocaleDateString(lang, {
    ...DATE_FORMAT_PRESETS[format],
    ...(timeZone && { timeZone }),
  });
};

const parseFormatOptions = (format: string) => {
  const match = format.match(/^([\w-]+)(?:\((.+)\))?$/);
  if (!match) {
    return { name: format, formatOptions: {} };
  }

  const [, name, optionsStr] = match;
  const formatOptions: Record<string, string> = {};

  if (optionsStr) {
    optionsStr.split(';').forEach((opt) => {
      const [key, val] = opt.split(':').map((s) => s.trim());
      if (key && val) {
        formatOptions[key] = val;
      }
    });
  }

  return { name, formatOptions };
};

const getDateOptions = (formatOptions: Record<string, string>) => ({
  preserveCalendarDate: formatOptions.preserveCalendarDate === 'true',
  timeZone: formatOptions.timeZone,
});

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .use(
    resourcesToBackend(
      (language: string, namespace: string) =>
        import(`./${language}/${namespace}.ts`),
    ),
  )
  .init({
    cleanCode: true,
    debug: false,
    defaultNS: 'core',
    fallbackLng: i18nConfig.defaultLocale,
    fallbackNS: 'core',
    nonExplicitSupportedLngs: true,
    ns: ['priority'],
    partialBundledLanguages: true,
    preload: [i18nConfig.defaultLocale],
    supportedLngs: i18nConfig.locales,
    interpolation: {
      // TODO: check with Kevin & Chris if we can do this
      // escapeValue: false, // Set to false to prevent i18next from escaping HTML entities
      format: (value, format, lang, options) => {
        if (!format) {
          return value;
        }

        const { name, formatOptions } = parseFormatOptions(format);

        const formatters = {
          [LOCALE_FORMATTERS.CURRENCY]: () =>
            // @ts-expect-error - this will be refactored in upcoming MR
            formatCurrency(value, lang, options),
          [LOCALE_FORMATTERS.CURRENCY_CUSTOM]: () =>
            stripEmptyDigits(value, lang),
          [LOCALE_FORMATTERS.DATE_LONG]: () =>
            formatDate('long', value, lang, getDateOptions(formatOptions)),
          [LOCALE_FORMATTERS.DATE_MONTH_YEAR]: () =>
            formatDate(
              'month-year',
              value,
              lang,
              getDateOptions(formatOptions),
            ),
          [LOCALE_FORMATTERS.DATE_YEARLESS]: () =>
            formatDate('yearless', value, lang, getDateOptions(formatOptions)),
          [LOCALE_FORMATTERS.VOWEL_ENDING]: () => endsWithVowel(value),
        };

        return isLocaleFormat(name) ? formatters[name]() : value;
      },
    },
  });

// TODO Save this for larger MR>
// SOLA_FORMATTERS.forEach(([name, cb]) => {
//   i18n.services?.formatter?.add(name, cb);
// });
export default i18n;
